import { useState, useRef, useEffect } from 'react';
import { X, ArrowUp, Loader2, ChefHat } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuthStore, useCurrentRestaurantId } from '@/stores/authStore';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  toolCall?: string;
}

const CONTEXT_SUGGESTIONS: Record<string, string[]> = {
  '/dashboard': ['¿Cómo va mi restaurante?', '¿Hay algo urgente?', 'Platos menos rentables'],
  '/ingredients': ['¿Qué ingredientes me cuestan más?', 'Stock bajo', 'Comparar con mercado'],
  '/recipes': ['Recetas en rojo', 'Ranking de rentabilidad', 'Sugiere precios'],
  '/stock': ['¿Cómo está mi stock?', 'Ingredientes por acabarse', 'Merma de esta semana'],
  '/reports': ['Informe de rentabilidad', '¿Dónde se me va el dinero?', 'Ingredientes sin usar'],
};

function getDefaultSuggestions(pathname: string): string[] {
  for (const [route, suggestions] of Object.entries(CONTEXT_SUGGESTIONS)) {
    if (pathname === route || pathname.startsWith(route + '/')) {
      return suggestions;
    }
  }
  return ['¿Cómo va mi restaurante?', '¿Mis platos menos rentables?', '¿Hay alertas?'];
}

const AGENT_URL = import.meta.env.VITE_AGENT_URL || 'https://api.pleko.app/agent';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function PinchePanel({ isOpen, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentTool, setCurrentTool] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { pathname } = useLocation();
  const { user, restaurants } = useAuthStore();
  const currentRestaurantId = useCurrentRestaurantId();

  const suggestions = getDefaultSuggestions(pathname);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentTool]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const sendMessage = async (text?: string) => {
    const msg = text || input.trim();
    if (!msg || isLoading) return;

    setInput('');
    const updatedMessages: Message[] = [...messages, { role: 'user', content: msg }];
    setMessages(updatedMessages);
    setIsLoading(true);
    setCurrentTool('Consultando datos...');

    try {
      const restaurant = restaurants.find(r => r.id === currentRestaurantId);
      const response = await fetch(`${AGENT_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent: 'pinche',
          message: msg,
          history: updatedMessages.slice(-10).map(m => ({ role: m.role, content: m.content })),
          restaurantName: restaurant?.name || 'tu restaurante',
        }),
      });

      const data = await response.json();

      if (data.toolsUsed?.length) {
        setCurrentTool(`Usando ${data.toolsUsed.join(', ')}...`);
        await new Promise(r => setTimeout(r, 300));
      }

      setCurrentTool(null);
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply || data.error || 'Sin respuesta' }]);
    } catch {
      setCurrentTool(null);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Chef, no he podido conectar con el servidor. Inténtalo de nuevo en unos segundos.',
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div
      className={`fixed top-0 right-0 h-full z-50 transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
      style={{ width: 420 }}
    >
      <div className="h-full bg-white border-l border-slate-200 flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #F97316, #EA580C)' }}
            >
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">El Pinche IA</div>
              <div className="text-xs text-slate-400">Tu currante de costes</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        {/* Chat area */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <div
                className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #FFF7ED, #FFEDD5)' }}
              >
                <ChefHat className="w-8 h-8 text-orange-500" />
              </div>
              <p className="text-slate-800 font-medium text-sm">
                ¡Oi, {user?.name?.split(' ')[0] || 'chef'}!
              </p>
              <p className="text-slate-400 text-xs mt-1">
                Soy tu pinche digital. Pregúntame lo que quieras.
              </p>
              <div className="mt-6 space-y-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => sendMessage(s)}
                    className="block w-full text-left text-xs px-4 py-2.5 bg-orange-50 text-orange-700 rounded-xl hover:bg-orange-100 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'gap-2.5'}`}>
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center shrink-0 mt-0.5">
                  <ChefHat className="w-3.5 h-3.5 text-orange-500" />
                </div>
              )}
              <div
                className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-blue-500 text-white rounded-2xl rounded-br-md'
                    : 'bg-slate-50 text-slate-700 rounded-2xl rounded-bl-md border border-slate-100'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <div
                    className="prose prose-sm prose-slate max-w-none [&_strong]:text-slate-900 [&_table]:text-xs"
                    dangerouslySetInnerHTML={{
                      __html: msg.content
                        .replace(/\n/g, '<br/>')
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                    }}
                  />
                ) : (
                  msg.content
                )}
              </div>
            </div>
          ))}

          {currentTool && (
            <div className="flex items-center gap-2 text-xs text-orange-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>{currentTool}</span>
            </div>
          )}

          {isLoading && !currentTool && (
            <div className="flex gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
                <ChefHat className="w-3.5 h-3.5 text-orange-500" />
              </div>
              <div className="bg-slate-50 px-4 py-3 rounded-2xl rounded-bl-md border border-slate-100">
                <Loader2 className="w-4 h-4 text-slate-300 animate-spin" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggestion chips after first response */}
        {messages.length > 0 && messages.length <= 4 && !isLoading && (
          <div className="px-5 pb-2 flex flex-wrap gap-2">
            {['Sugiere precios', 'Ver recetas afectadas', 'Comparar mercado'].map((chip) => (
              <button
                key={chip}
                onClick={() => sendMessage(chip)}
                className="text-xs px-3 py-1.5 rounded-full bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors font-medium"
              >
                {chip}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="border-t border-slate-100 px-4 py-3">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Pregunta sobre tu restaurante..."
              rows={1}
              className="flex-1 text-sm px-4 py-2.5 border border-slate-200 rounded-2xl resize-none focus:outline-none focus:border-orange-300 focus:ring-1 focus:ring-orange-300 max-h-24"
              disabled={isLoading}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || isLoading}
              className="w-9 h-9 flex items-center justify-center rounded-full text-white transition-all disabled:opacity-30 shrink-0"
              style={{ background: input.trim() ? 'linear-gradient(135deg, #F97316, #EA580C)' : '#CBD5E1' }}
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
